import {
	BUDGET_RANGE_OPTIONS,
	bahrainPhoneRegex,
	type ContactFormData,
	contactFormSchema,
	PROJECT_TYPE_OPTIONS,
	TIMELINE_OPTIONS,
} from "@designs-by-maryam/types/contact";
import { Button } from "@designs-by-maryam/ui/components/button";
import {
	Field,
	FieldError,
	FieldLabel,
} from "@designs-by-maryam/ui/components/field";
import { Input } from "@designs-by-maryam/ui/components/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@designs-by-maryam/ui/components/select";
import { Textarea } from "@designs-by-maryam/ui/components/textarea";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { client } from "@/utils/orpc";

/**
 * Field-level validation schemas for onBlur validation (AC-2.6.2)
 * Using Zod directly with TanStack Form's Standard Schema support
 */
const fieldValidators = {
	name: z.string().min(2, "Name must be at least 2 characters"),
	email: z.string().email("Please enter a valid email address"),
	phone: z
		.string()
		.regex(bahrainPhoneRegex, "Please enter a valid Bahrain phone number"),
	projectType: z.enum([
		"residential",
		"commercial",
		"hospitality",
		"consultation",
	]),
	details: z
		.string()
		.min(10, "Please provide at least 10 characters about your project"),
};

type ContactFormProps = {
	defaultProjectType?: string;
};

export function ContactForm({ defaultProjectType }: ContactFormProps) {
	const formId = useId();

	const submitMutation = useMutation({
		mutationFn: async (data: ContactFormData) => client.submitContactForm(data),
		onSuccess: () => {
			toast.success("Thank you! We'll contact you within 24 hours.");
			form.reset();
		},
		onError: (error: Error) => {
			toast.error(
				error.message || "Failed to submit contact form. Please try again.",
			);
		},
	});

	const form = useForm({
		defaultValues: {
			name: "",
			email: "",
			phone: "",
			projectType:
				(defaultProjectType as ContactFormData["projectType"]) ||
				("" as ContactFormData["projectType"]),
			budgetRange: "",
			timeline: "",
			details: "",
		},
		validators: {
			// Form-level validation on submit using shared API schema (M1 fix: DRY)
			onSubmit: ({ value }) => {
				const result = contactFormSchema.safeParse({
					...value,
					budgetRange: value.budgetRange || undefined,
					timeline: value.timeline || undefined,
				});
				if (!result.success) {
					return result.error.issues[0]?.message ?? "Validation failed";
				}
				return;
			},
		},
		onSubmit: ({ value }) => {
			// Convert empty strings to undefined for optional fields
			const formData: ContactFormData = {
				...value,
				budgetRange: value.budgetRange || undefined,
				timeline: value.timeline || undefined,
			};
			submitMutation.mutate(formData);
		},
	});

	return (
		<form
			className="space-y-6"
			onSubmit={(e) => {
				e.preventDefault();
				form.handleSubmit();
			}}
		>
			{/* Name and Email Row */}
			<div className="grid gap-6 md:grid-cols-2">
				{/* Name Field */}
				<form.Field name="name" validators={{ onBlur: fieldValidators.name }}>
					{(field) => {
						const hasError = field.state.meta.errors.length > 0;
						const isFilled = field.state.value.length > 0;
						const errorId = `${formId}-name-error`;
						const errors = field.state.meta.errors.map((e) => ({
							message: String(e),
						}));
						return (
							<Field className="relative" data-invalid={hasError || undefined}>
								<FieldLabel
									className="transition-all duration-200 peer-focus:text-primary"
									htmlFor={`${formId}-name`}
								>
									Name <span className="text-destructive">*</span>
								</FieldLabel>
								<Input
									aria-describedby={hasError ? errorId : undefined}
									aria-invalid={hasError}
									className="peer transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
									id={`${formId}-name`}
									name={field.name}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="Your full name"
									value={field.state.value}
								/>
								{isFilled && !hasError && (
									<div className="absolute top-9 right-3 text-primary">
										<svg
											aria-hidden="true"
											className="h-5 w-5"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											viewBox="0 0 24 24"
										>
											<path
												d="M5 13l4 4L19 7"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
								<FieldError errors={errors} id={errorId} />
							</Field>
						);
					}}
				</form.Field>

				{/* Email Field */}
				<form.Field name="email" validators={{ onBlur: fieldValidators.email }}>
					{(field) => {
						const hasError = field.state.meta.errors.length > 0;
						const isFilled = field.state.value.length > 0;
						const errorId = `${formId}-email-error`;
						const errors = field.state.meta.errors.map((e) => ({
							message: String(e),
						}));
						return (
							<Field className="relative" data-invalid={hasError || undefined}>
								<FieldLabel
									className="transition-all duration-200 peer-focus:text-primary"
									htmlFor={`${formId}-email`}
								>
									Email <span className="text-destructive">*</span>
								</FieldLabel>
								<Input
									aria-describedby={hasError ? errorId : undefined}
									aria-invalid={hasError}
									className="peer transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
									id={`${formId}-email`}
									name={field.name}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="your@email.com"
									type="email"
									value={field.state.value}
								/>
								{isFilled && !hasError && (
									<div className="absolute top-9 right-3 text-primary">
										<svg
											aria-hidden="true"
											className="h-5 w-5"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											viewBox="0 0 24 24"
										>
											<path
												d="M5 13l4 4L19 7"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
								<FieldError errors={errors} id={errorId} />
							</Field>
						);
					}}
				</form.Field>
			</div>

			{/* Phone and Project Type Row */}
			<div className="grid gap-6 md:grid-cols-2">
				{/* Phone Field */}
				<form.Field name="phone" validators={{ onBlur: fieldValidators.phone }}>
					{(field) => {
						const hasError = field.state.meta.errors.length > 0;
						const isFilled = field.state.value.length > 0;
						const errorId = `${formId}-phone-error`;
						const errors = field.state.meta.errors.map((e) => ({
							message: String(e),
						}));
						return (
							<Field className="relative" data-invalid={hasError || undefined}>
								<FieldLabel
									className="transition-all duration-200 peer-focus:text-primary"
									htmlFor={`${formId}-phone`}
								>
									Phone <span className="text-destructive">*</span>
								</FieldLabel>
								<Input
									aria-describedby={hasError ? errorId : undefined}
									aria-invalid={hasError}
									className="peer transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
									id={`${formId}-phone`}
									name={field.name}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="+973 XXXX XXXX"
									type="tel"
									value={field.state.value}
								/>
								{isFilled && !hasError && (
									<div className="absolute top-9 right-3 text-primary">
										<svg
											aria-hidden="true"
											className="h-5 w-5"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											viewBox="0 0 24 24"
										>
											<path
												d="M5 13l4 4L19 7"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
								<FieldError errors={errors} id={errorId} />
							</Field>
						);
					}}
				</form.Field>

				{/* Project Type Field */}
				<form.Field
					name="projectType"
					validators={{ onBlur: fieldValidators.projectType }}
				>
					{(field) => {
						const hasError = field.state.meta.errors.length > 0;
						const errorId = `${formId}-projectType-error`;
						const errors = field.state.meta.errors.map((e) => ({
							message: String(e),
						}));
						return (
							<Field data-invalid={hasError || undefined}>
								<FieldLabel
									className="transition-all duration-200"
									htmlFor={`${formId}-projectType`}
								>
									Project Type <span className="text-destructive">*</span>
								</FieldLabel>
								<Select
									name={field.name}
									onValueChange={(value) =>
										field.handleChange(value as ContactFormData["projectType"])
									}
									value={field.state.value}
								>
									<SelectTrigger
										aria-describedby={hasError ? errorId : undefined}
										aria-invalid={hasError}
										className="w-full transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
										id={`${formId}-projectType`}
									>
										<SelectValue placeholder="Select project type" />
									</SelectTrigger>
									<SelectContent>
										{PROJECT_TYPE_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FieldError errors={errors} id={errorId} />
							</Field>
						);
					}}
				</form.Field>
			</div>

			{/* Budget and Timeline Row (Optional) */}
			<div className="grid gap-6 md:grid-cols-2">
				{/* Budget Range Field */}
				<form.Field name="budgetRange">
					{(field) => (
						<Field>
							<FieldLabel
								className="transition-all duration-200"
								htmlFor={`${formId}-budgetRange`}
							>
								Budget Range
							</FieldLabel>
							<Select
								name={field.name}
								onValueChange={(value) => field.handleChange(value)}
								value={field.state.value ?? ""}
							>
								<SelectTrigger
									className="w-full transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
									id={`${formId}-budgetRange`}
								>
									<SelectValue placeholder="Select budget range (optional)" />
								</SelectTrigger>
								<SelectContent>
									{BUDGET_RANGE_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					)}
				</form.Field>

				{/* Timeline Field */}
				<form.Field name="timeline">
					{(field) => (
						<Field>
							<FieldLabel
								className="transition-all duration-200"
								htmlFor={`${formId}-timeline`}
							>
								Timeline
							</FieldLabel>
							<Select
								name={field.name}
								onValueChange={(value) => field.handleChange(value)}
								value={field.state.value ?? ""}
							>
								<SelectTrigger
									className="w-full transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
									id={`${formId}-timeline`}
								>
									<SelectValue placeholder="Select timeline (optional)" />
								</SelectTrigger>
								<SelectContent>
									{TIMELINE_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					)}
				</form.Field>
			</div>

			{/* Project Details Field */}
			<form.Field
				name="details"
				validators={{ onBlur: fieldValidators.details }}
			>
				{(field) => {
					const hasError = field.state.meta.errors.length > 0;
					const isFilled = field.state.value.length > 0;
					const errorId = `${formId}-details-error`;
					const errors = field.state.meta.errors.map((e) => ({
						message: String(e),
					}));
					return (
						<Field className="relative" data-invalid={hasError || undefined}>
							<FieldLabel
								className="transition-all duration-200 peer-focus:text-primary"
								htmlFor={`${formId}-details`}
							>
								Project Details <span className="text-destructive">*</span>
							</FieldLabel>
							<Textarea
								aria-describedby={hasError ? errorId : undefined}
								aria-invalid={hasError}
								className="peer min-h-32 transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:focus:border-primary"
								id={`${formId}-details`}
								name={field.name}
								onBlur={field.handleBlur}
								onChange={(e) => field.handleChange(e.target.value)}
								placeholder="Tell us about your project, including any specific requirements, inspiration, or questions you have..."
								value={field.state.value}
							/>
							{isFilled && !hasError && (
								<div className="absolute top-9 right-3 text-primary">
									<svg
										aria-hidden="true"
										className="h-5 w-5"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										viewBox="0 0 24 24"
									>
										<path
											d="M5 13l4 4L19 7"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</div>
							)}
							<FieldError errors={errors} id={errorId} />
						</Field>
					);
				}}
			</form.Field>

			{/* Submit Button */}
			<form.Subscribe
				selector={(state) => [state.canSubmit, state.isSubmitting]}
			>
				{([canSubmit, isFormSubmitting]) => (
					<Button
						className="group w-full transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 md:w-auto"
						disabled={!canSubmit || submitMutation.isPending}
						size="lg"
						type="submit"
					>
						{submitMutation.isPending || isFormSubmitting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Sending...
							</>
						) : (
							<>
								Send Message
								<svg
									aria-hidden="true"
									className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									viewBox="0 0 24 24"
								>
									<path
										d="M5 12h14M12 5l7 7-7 7"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</>
						)}
					</Button>
				)}
			</form.Subscribe>
		</form>
	);
}
